// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Chart specs for the Live view.
 */

import {
  formatUnit,
  getParameterChartSpec,
  toFahrenheit,
  BASELINE_STRATEGY,
  RECOMMENDED_STRATEGY,
  comparisonFrom,
  findSolution,
  type PlanChartSpec,
  type SeriesInterpolation,
  type SeriesVariant,
} from './planSeries';
import { hvacMemberIds } from './logicalNodeResolution';
import type { PlanEquipment, PlanSolution, RawInputs } from '../types';

/** Metered electrical power on a chiller/pump/CRAH, in kW. */
const POWER_SIGNAL = 'brick:Electric_Power_Sensor';
/** Metered active power at the switchgear, in kW. */
const GRID_POWER_SIGNAL = 'wattsch:ActivePower';
const SWITCHGEAR_TYPE = 'wattsch:SwitchgearType';
const AMBIENT_SENSOR = 'brick:Outside_Air_Temperature_Sensor';
/** Total forecast IT active power across the site's racks, in kW. */
const IT_LOAD_SIGNAL = 'wattsch:ActivePower';

/** Element-wise sum of a solve's members, ignoring absent signals. */
function sumMemberSignal(
  solution: PlanSolution | undefined,
  memberIds: string[],
  signal: string
): { values: number[]; unit?: string } | undefined {
  const wanted = new Set(memberIds);
  const arrays: number[][] = [];
  let unit: string | undefined;

  for (const equipment of solution?.equipment ?? []) {
    if (!wanted.has(equipment.id)) continue;
    const node = (equipment as PlanEquipment).state?.[signal];
    if (!Array.isArray(node?.value)) continue;
    arrays.push(node.value.map(Number));
    unit = node.unit ?? unit;
  }
  if (arrays.length === 0) return undefined;

  const length = Math.max(...arrays.map(a => a.length));
  const values = Array.from({ length }, (_, i) =>
    arrays.reduce((total, a) => total + (Number.isFinite(a[i]) ? a[i] : 0), 0)
  );
  return { values, unit };
}

/**
 * Baseline-vs-recommended HVAC **electrical power** in kW, summed across the
 * plan's combined-HVAC member set (chillers, pumps and CRAHs).
 */
export function buildHvacLoadKwSpec(rawInputs: RawInputs | undefined): PlanChartSpec | undefined {
  const memberIds = hvacMemberIds(rawInputs);
  if (memberIds.length === 0) return undefined;
  return comparisonFrom(
    sumMemberSignal(findSolution(rawInputs, BASELINE_STRATEGY), memberIds, POWER_SIGNAL),
    sumMemberSignal(findSolution(rawInputs, RECOMMENDED_STRATEGY), memberIds, POWER_SIGNAL),
    'Forecasted baseline (electric)',
    'Forecasted recommendation (electric)'
  );
}

/** Baseline-vs-recommended grid intake in kW, off the single switchgear. */
export function buildTotalGridLoadSpec(
  rawInputs: RawInputs | undefined
): PlanChartSpec | undefined {
  const read = (solution: PlanSolution | undefined) => {
    const equipment = solution?.equipment?.find(e => e.type === SWITCHGEAR_TYPE);
    const node = equipment?.state?.[GRID_POWER_SIGNAL];
    return Array.isArray(node?.value)
      ? { values: node.value.map(Number), unit: node.unit }
      : undefined;
  };
  return comparisonFrom(
    read(findSolution(rawInputs, BASELINE_STRATEGY)),
    read(findSolution(rawInputs, RECOMMENDED_STRATEGY))
  );
}

/** Forecast ambient temperature, converted to °F for the operator-facing card. */
export function buildAmbientTempSpec(rawInputs: RawInputs | undefined): PlanChartSpec | undefined {
  const signal = rawInputs?.weather?.[AMBIENT_SENSOR];
  if (!Array.isArray(signal?.value) || signal.value.length === 0) return undefined;
  return toFahrenheit({
    series: [{ label: 'Forecast', values: signal.value, variant: 'single' }],
    unit: formatUnit(signal.unit),
  });
}

/**
 * Forecast IT load over the day, in kW.
 */
export function buildItLoadSpec(rawInputs: RawInputs | undefined): PlanChartSpec | undefined {
  const forecast = rawInputs?.it_load_forecast;
  if (!forecast) return undefined;

  // The named signal first; otherwise the first signal that carries a curve, so
  // a payload that renames it still charts instead of silently dropping out.
  const signal =
    forecast[IT_LOAD_SIGNAL] ?? Object.values(forecast).find(s => Array.isArray(s?.value));
  if (!Array.isArray(signal?.value) || signal.value.length === 0) return undefined;

  return {
    series: [{ label: 'Forecast', values: signal.value.map(Number), variant: 'single' }],
    unit: formatUnit(signal.unit),
  };
}

/**
 * Baseline-vs-recommended **thermal** cooling output in kW.
 */
export function buildCoolingLoadSpec(rawInputs: RawInputs | undefined): PlanChartSpec | undefined {
  const spec = getParameterChartSpec('total_cooling_load_kw', rawInputs);
  if (!spec) return undefined;
  return {
    ...spec,
    series: spec.series.map(s => ({ ...s, label: `${s.label} (thermal)` })),
  };
}

/** Fleet-average chilled-water setpoint, in °F. */
export function buildSetpointSpec(rawInputs: RawInputs | undefined): PlanChartSpec | undefined {
  return toFahrenheit(getParameterChartSpec('avg_chilled_water_setpoint_c', rawInputs));
}

/**
 * How many chillers the solve runs at each timestep.
 */
export function buildStagingSpec(rawInputs: RawInputs | undefined): PlanChartSpec | undefined {
  const spec = getParameterChartSpec('chillers_on_count', rawInputs);
  if (!spec) return undefined;
  return { ...spec, series: spec.series.map(s => ({ ...s, interpolation: 'step' as const })) };
}

/**
 * Tariff price over the day, when the optimizer published one.
 */
const TARIFF_SIGNALS = ['tariff', 'energy_price', 'price', 'tariff_price'];

export function buildTariffSpec(rawInputs: RawInputs | undefined): PlanChartSpec | undefined {
  for (const solution of rawInputs?.solutions ?? []) {
    for (const aggregate of solution.aggregated_summary ?? []) {
      for (const name of TARIFF_SIGNALS) {
        const kpi = aggregate.kpi?.[name];
        if (kpi && Array.isArray(kpi.value) && kpi.value.length > 0) {
          return {
            series: [{ label: 'Tariff', values: kpi.value, variant: 'single' }],
            unit: formatUnit(kpi.unit) || '$/kWh',
          };
        }
      }
    }
  }
  return undefined;
}

/**
 * Add a live (measured) curve to a forecast spec. The live series leads so it
 * draws over the forecast lines, and an all-null live series is left out
 * entirely rather than adding a legend entry for a curve with nothing in it.
 */
export function withLiveSeries(
  spec: PlanChartSpec | undefined,
  values: (number | null)[] | undefined,
  label = 'Live',
  variant: SeriesVariant = 'live',
  options: { interpolation?: SeriesInterpolation } = {}
): PlanChartSpec | undefined {
  if (!spec) return undefined;
  if (!values?.some(v => v !== null && Number.isFinite(v))) return spec;
  return {
    ...spec,
    series: [{ label, values, variant, interpolation: options.interpolation }, ...spec.series],
  };
}

/**
 * A forecast spec with the recommended solve taken out, for a day no plan was
 * approved to run.
 */
export function baselineOnly(spec: PlanChartSpec | undefined): PlanChartSpec | undefined {
  if (!spec) return undefined;
  // Labels stay as they are: the legend is tight, and the banner above the
  // grid already says the baseline is what the site is running.
  const series = spec.series.filter(s => s.variant !== 'recommended');
  return series.length > 0 ? { ...spec, series } : undefined;
}

/**
 * {@link withLiveSeries} for a card that may have no forecast at all: the
 * measured curve charts on its own, in `unit`, instead of the card falling back
 * to its absent note. Still undefined when there is neither.
 */
export function withLiveSeriesOrAlone(
  spec: PlanChartSpec | undefined,
  values: (number | null)[] | undefined,
  unit: string,
  label = 'Live',
  options: { interpolation?: SeriesInterpolation } = {}
): PlanChartSpec | undefined {
  const merged = withLiveSeries(spec ?? { series: [], unit }, values, label, 'live', options);
  return merged && merged.series.length > 0 ? merged : undefined;
}
